/**
 * The plotting banner. The manual-plotting palette now lives in the merged
 * Plotting workspace (PlanningSheet, the "Plot" tab); all that remains here is
 * the banner shown while a tap-to-place tool is armed, so it's always clear we
 * are in a draw state and how to leave it. It's driven by the active tool, not
 * the mode, so it works wherever a tool was armed.
 */
import { useChartPlotterStore, type ActiveTool } from '@/store';
import { useShellStore } from '@/store/shell';
import { useObjectsStore } from '@/store/objects';

const TOOL_HELP: Partial<Record<ActiveTool, string>> = {
  dr: 'Tap the chart to place the dead-reckoning position.',
  ep: 'Tap the chart to place the estimated position, with the scenario tide applied.',
  vrm: 'Tap the chart to set the variable range marker from the boat.',
  ebl: 'Tap the chart to set the electronic bearing line from the boat.',
};

const TOOL_NAME: Partial<Record<ActiveTool, string>> = {
  dr: 'Dead reckoning',
  ep: 'Estimated position',
  vrm: 'Variable range marker',
  ebl: 'Electronic bearing line',
};

export function PlotBanner() {
  const activeTool = useChartPlotterStore((s) => s.activeTool);
  const setActiveTool = useChartPlotterStore((s) => s.setActiveTool);
  const showToast = useShellStore((s) => s.showToast);
  const canUndo = useObjectsStore((s) => s.past.length > 0);
  const undo = useObjectsStore((s) => s.undo);

  const armed = activeTool in TOOL_HELP;
  if (!armed) return null;

  return (
    <div className="acp-plot-banner">
      <div className="pb-txt">
        <b>Plotting: {TOOL_NAME[activeTool]}</b>
        <span>{TOOL_HELP[activeTool]}</span>
      </div>
      {canUndo && (
        <button
          type="button"
          className="pb-done"
          onClick={() => {
            undo();
            showToast('Undid last plot');
          }}
        >
          Undo
        </button>
      )}
      <button
        type="button"
        className="pb-done"
        onClick={() => {
          setActiveTool('pan');
          showToast('Plotting finished');
        }}
      >
        Done
      </button>
    </div>
  );
}
